/** @license
 *
 * Copyright 2022 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * CardsFetcher is the interface between the app and the source of Solution cards. It manages the fetching, filtering,
 * and merging of the multiple sources to present a single paged source to the app. It provides two ways of filling
 * the cache: available and deployed solutions.
 *
 * @module CardsFetcher
 */

//#region Interfaces

import * as cardsHelpers from "./cardsHelpers";
import * as common from "@esri/solution-common";
import * as helpers from "./helpers";
import * as portal from "@esri/arcgis-rest-portal";
import * as restTypes from "@esri/arcgis-rest-types";

interface ICardCacheParams {
  q: string,
  categoriesAsStr: string;
  sortField: string;
  sortOrder: string;
}

export interface ISearchOptionPartial {
  "q": string; // for full-text search and query parameters not supported by filter
  "filter": string; // for queries on exact values
  "sortField": string;
  "sortOrder": string;
  "nextStart"?: any;
  "categories": string[];
  "delay": boolean;
  "getSuggested"?: boolean;
}

//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

export class CardsFetcher {

  private readonly cMissingImageDataURI = "https://gis.dwa.org/portal/portalimages/solution.png";

  private cardCache: cardsHelpers.ISolutionInfoCard[];
  private cardCacheParams: ICardCacheParams;
  private locale: string;
  private orgUrl: string;
  private portalUrl: string;
  private agolPortalUrl: string;
  private anonAgolSession: common.UserSession | null;


  /**
   * Constructor
   *
   * @param orgUrl URL of organization, e.g., "http://myOrg.maps.arcgis.com"
   * @param portalUrl URL of portal, e.g., "https://www.arcgis.com"
   * @param userSession Authentication
   */
  public constructor(
    locale: string,
    orgUrl: string,
    portalUrl: string
  ) {
    this.cardCache = [];
    this.cardCacheParams = {
      q: "",
      categoriesAsStr: "[]",
      sortField: "title",
      sortOrder: "asc"
    };
    this.locale = locale;
    this.orgUrl = orgUrl;
    this.portalUrl = portalUrl;
    //Setup anonymous arcgis online user session for query to public enteprise connected apps group
    this.anonAgolSession = new common.UserSession({});
    this.agolPortalUrl = "https://www.arcgis.com"
  }

  /**
   * Adds a card to the cache to keep cache fresh between full refreshes.
   *
   * @param card Card to add
   */
  public addToCache(
    card: cardsHelpers.ISolutionInfoCard
  ): void {
    this.cardCache.push(card);
  }

  /**
   * Returns the current cache.
   *
   * @returns Cards in the cache
   */
  public getCache(
  ): cardsHelpers.ISolutionInfoCard[] {
    return this.cardCache;
  }

  /**
   * Removes a card to the cache to keep cache fresh between full refreshes.
   *
   * @param id Id of card to remove
   */
  public removeFromCache(
    id: string
  ): void {
    this.cardCache = this.cardCache.filter(card => card.id !== id);
  }

  /**
   * Fetches cards for availale Solutions from the server.
   *
   * @param query AGO query terms and options
   * @param primarySolutionsGroupId Id of a group that's the primary source of Solutions for AGO or Enterprise
   * @param agoBasedEnterpriseSolutionsGroupId Id of an AGO-based set of Enterprise Solutions
   * @param userSession Authentication for thumbnailUrl
   * @param pagingParams Parameters for how the request is to be batched
   * @param acceptableIds A set of item ids that the results must be a part of; if not defined, then there's no restriction
   * @returns A promise resolving to a collection of available Solutions as cards as well as paging information about
   * where those cards came from in the fetcher cache; in case of failure, the promise resolves to an error message
   */
  public requestAvailableSolutionCards(
    query: ISearchOptionPartial,
    primarySolutionsGroupId: string,
    agoBasedEnterpriseSolutionsGroupId: string,
    userSession: common.UserSession | undefined,
    pagingParams?: restTypes.IPagingParams,
    acceptableIds?: Set<string>
  ): Promise<cardsHelpers.ICacheBatch<cardsHelpers.ISolutionInfoCard>> {
    const start1 = (pagingParams?.start ? pagingParams.start : 1); // note that this is a 1-based index
    const num = pagingParams?.num ? pagingParams.num : 24;
    const portalUrl = this.portalUrl + "/sharing/rest";
    const agolPortalUrl = this.agolPortalUrl + "/sharing/rest";
    const anonAgolSession = this.anonAgolSession as common.UserSession;

    // Have the query string or the categories params changed? If so, our cache is invalid.
    // (We'll manage sort changes by modifying the cache.)
    // For the time being, we'll also trigger a refresh when sorting by relevance because we want the server to do the sort.
    const categoriesAsStr = JSON.stringify(query.categories);
    if (this.cardCacheParams.q !== query.q
        || this.cardCacheParams.categoriesAsStr !== categoriesAsStr
        || query.sortField === "relevance") {
      this.cardCache = [];
      this.cardCacheParams = {
        q: query.q,
        categoriesAsStr,
        sortField: query.sortField,
        sortOrder: query.sortOrder
      };

    // Have the sort params changed?
    } else if (
      this.cardCacheParams.sortField !== query.sortField ||
      this.cardCacheParams.sortOrder !== query.sortOrder
    ) {
      this.cardCacheParams.sortField = query.sortField;
      this.cardCacheParams.sortOrder = query.sortOrder;
      this.cardCache = cardsHelpers.sortCache(
        this.cardCache, this.cardCacheParams.sortField, this.cardCacheParams.sortOrder
      ) as cardsHelpers.ISolutionInfoCard[];
    }

    // If we have a cache, we can satisfy the request from the cache.
    if (this.cardCache.length > 0) {
      let cardsBatch: cardsHelpers.ICacheBatch<cardsHelpers.ISolutionInfoCard>;
      if (acceptableIds && acceptableIds.size > 0) {
        // We need to make a sub-cache that's filtered thru the acceptableIds
        const cardSubcache = cardsHelpers.createFilteredSubcache(this.cardCache, acceptableIds);
        cardsBatch = cardsHelpers.extractFromCache(cardSubcache, start1, num);
      } else {
        cardsBatch = cardsHelpers.extractFromCache(this.cardCache, start1, num);
      }
      return Promise.resolve(cardsBatch);
    }

    // Otherwise, we have to fill the cache to cover the request. Because we don't know in advance how the two
    // potential sources will merge, we have to get all of the results from both sources.
    const additionalSearchOptions = {
      filter: query.filter,
      sortField: query.sortField,
      sortOrder: query.sortOrder,
      categories: query?.categories ? this.convertSearchCategories(query.categories) : ""
    };

    // Trigger parallel searches of groups `primarySolutionsGroupId` and `agoBasedEnterpriseSolutionsGroupId`
    // if supplied
    const searches: Array<Promise<portal.ISearchResult<common.IItem>>> = [];
    if (primarySolutionsGroupId) {
      const auth = userSession ? userSession : new common.UserSession({});
      searches.push(
        common.searchGroupAllContents(primarySolutionsGroupId, query.q, auth,
          additionalSearchOptions as common.IAdditionalGroupSearchOptions, portalUrl)
      );
    }
    if (agoBasedEnterpriseSolutionsGroupId) {
      searches.push(
        common.searchGroupAllContents(agoBasedEnterpriseSolutionsGroupId, query.q, anonAgolSession,
          additionalSearchOptions as common.IAdditionalGroupSearchOptions, agolPortalUrl)
      );
    }

    // We're done with the searches; refill the cache
    return Promise.allSettled(searches)
    .then((responseSet: Array<PromiseSettledResult<common.ISearchResult<common.IItem>>>) => {
      const fulfilledResponses: Array<common.ISearchResult<common.IItem>> = [];

      // Extract and augment the results
      responseSet.forEach((result, index) => {
        // Promise.allSettled() maintains the order of the template card search result groups, so if the
        // primarySolutionsGroupId is defined, the first result will be the primary one
        const isPrimary = !!primarySolutionsGroupId && index === 0;

        if (result.status === 'fulfilled') {
          const searchResult = result.value;
          let templateSourceUrl = "";
          let knownToBePublicSource = false;

          if (isPrimary) {
            // The first group comes from the source portal url
            // In enterprise this is the local group on the enterprise machine containing 'disconnected' solutions
            // In AGOL this is default
            templateSourceUrl = this.portalUrl;
          } else {
            // In enterprise, the second group is the AGOL based template group (connected solutions)
            // In AGOL a second group is never queried
            templateSourceUrl = this.agolPortalUrl;
            knownToBePublicSource = true;
          }

          // We add a source portal URL here so that it is attached to each card and can be referenced later on
          // Also add a flag indicating if we can be sure to not need a token to fetch info about the item
          searchResult.results.forEach(item => {
            item.sourcePortalUrl = templateSourceUrl;
            item.knownToBePublicSource = knownToBePublicSource;
          })
          fulfilledResponses.push(searchResult);

        } else if (result.status === 'rejected') {
          throw new Error(
            (isPrimary
            // A failed fetch of the primary group in enterprise or AGO
            ? result.reason.message
            // We're in enterprise and configured to search AGO (because agoBasedEnterpriseSolutionsGroupId
            // is defined), but appear not to be able to reach the internet
            : "enterpriseCannotAccessAGO"
            ) as string
          );
        }
      })

      // Get the cards from the results
      if (fulfilledResponses.length === 0) {
        this.cardCache = [] as cardsHelpers.ISolutionInfoCard[];

      } else if (fulfilledResponses.length === 1) {
        // A single response has been sorted for us by the server
        this.cardCache = this.getCardsFromResponse(fulfilledResponses[0]);

      } else {
        // For two responses, merging requires us to sort even though each response has been sorted by the server
        this.cardCache = cardsHelpers.sortCache(
          this.getCardsFromResponse(this.mergeResponses(fulfilledResponses)),
          this.cardCacheParams.sortField,
          this.cardCacheParams.sortOrder
        ) as cardsHelpers.ISolutionInfoCard[];
      }

      // Get the requested batch from the cache
      let cardsBatch: cardsHelpers.ICacheBatch<cardsHelpers.ISolutionInfoCard>;
      if (acceptableIds && acceptableIds.size > 0) {
        // We need to make a sub-cache that's filtered thru the acceptableIds
        const cardSubcache = cardsHelpers.createFilteredSubcache(this.cardCache, acceptableIds);
        cardsBatch = cardsHelpers.extractFromCache(cardSubcache, start1, num);
      } else {
        cardsBatch = cardsHelpers.extractFromCache(this.cardCache, start1, num);
      }
      return Promise.resolve(cardsBatch);
    })
    .catch((error) => {
      return Promise.reject(new Error(error.message));
    });
  }

  /**
   * Fetches cards for deployed Solutions from the server.
   *
   * @param query AGO query terms and options
   * @param userSession Authentication for thumbnailUrl
   * @returns A collection of deployed Solutions as cards; the paging information shows that all of the cards
   * are returned
   */
  public requestDeployedSolutionCards(
    query: ISearchOptionPartial,
    userSession: common.UserSession | undefined,
  ): Promise<cardsHelpers.ICacheBatch<cardsHelpers.ISolutionInfoCard>> {
    const portalUrl = this.portalUrl + "/sharing/rest";

    // Have the query string or the categories params changed? If so, our cache is invalid.
    // (We'll manage sort changes by modifying the cache.)
    // For the time being, we'll also trigger a refresh when sorting by relevance because we want the server to do the sort.
    if (this.cardCacheParams.q !== query.q || query.sortField === "relevance") {
      this.cardCache = [];
      this.cardCacheParams = {
        q: query.q,
        categoriesAsStr: "[]",
        sortField: query.sortField,
        sortOrder: query.sortOrder
      };

    // Have the sort params changed?
    } else if (
      this.cardCacheParams.sortField !== query.sortField ||
      this.cardCacheParams.sortOrder !== query.sortOrder
    ) {
      this.cardCacheParams.sortField = query.sortField;
      this.cardCacheParams.sortOrder = query.sortOrder;
      this.cardCache = cardsHelpers.sortCache(
        this.cardCache, this.cardCacheParams.sortField, this.cardCacheParams.sortOrder
      ) as cardsHelpers.ISolutionInfoCard[];
    }

    // If we have a cache, we can satisfy the request from the cache.
    if (this.cardCache.length > 0) {
      const cardsBatch = cardsHelpers.extractFromCache(this.cardCache, 1, this.cardCache.length);
      return Promise.resolve(cardsBatch);
    }

    // Otherwise, we have to fill the cache to cover the request
    const searchOptions = {
      q: query.q,
      filter: query.filter,
      sortField: query.sortField,
      sortOrder: query.sortOrder,
      authentication: userSession,
      portal: portalUrl,
      categories: query?.categories ? JSON.stringify(query.categories) : ""
    } as portal.ISearchOptions;

    // We're done with the searches; refill the cache

    return common.searchAllItems(searchOptions as any)
    .then((response: portal.ISearchResult<common.IItem>) => {
      this.cardCache = this.getCardsFromResponse(response);

      // Get the requested batch from the cache
      const cardsBatch = cardsHelpers.extractFromCache(this.cardCache, 1, this.cardCache.length);
      return Promise.resolve(cardsBatch);
    })
    .catch((err) => {
      return Promise.reject(new Error(err));
    });
  }

  // ---------------------------------------------------------------------------------------------------------------- //

  /**
   * Stringifies categories for search.
   *
   * @param categories AGO item categories, e.g., "/Categories/Conservation"
   * or ["/Categories/Conservation", "/Categories/Pipeline"]
   * @returns Categories where each element is a stringified array of categories
   */
  private convertSearchCategories(
    categories: string | string[] | undefined
  ): string | string[] | undefined {
    /* istanbul ignore else */
    if (categories) {
      if (Array.isArray(categories)) {
        categories = categories.map(
          andGroup => JSON.stringify(Array.isArray(andGroup) ? andGroup : [andGroup])  // force to an array to allow categories with commas
        );
      } else {
        categories = JSON.stringify([categories]);
      }
    }
    return categories;
  }

  /**
   * Constructs Solution cards from a Solution-query response.
   *
   * @param response Response from a query
   * @returns Solution information as cards
   */
  private getCardsFromResponse(
    response: portal.ISearchResult<common.IItem>
  ): cardsHelpers.ISolutionInfoCard[] {
    if (response.results.length > 0) {
      const screenedCards = common.sanitizeJSON(response.results);

      // Transfer the query results into cards
      const cards: cardsHelpers.ISolutionInfoCard[] = screenedCards.map(
        (result: any) => {
          const card: cardsHelpers.ISolutionInfoCard = {
            id: result.id,
            title: result.title,
            snippet: result.snippet || "",
            description: result.description || "",
            url: result.url || "",
            itemUrl: this.orgUrl + "/home/item.html" + helpers.createUrlParams([{ k: "id", v: result.id }]) || "",
            thumbnailFilename: result.thumbnail,
            thumbnailUrl: helpers.generateThumbnailUrl(result.id as string, result.thumbnail as string,
              this.cMissingImageDataURI, (result.sourcePortalUrl ? result.sourcePortalUrl : this.portalUrl) as string),
            tryitUrl: result.url || "",
            typeKeywords: result.typeKeywords,
            learnMoreUrl: result.properties?.learnMoreUrl || "",
            createdMs: result.created as number,
            created: helpers.getLocaleLongDateString(result.created as number, this.locale),
            modified: helpers.getLocaleLongDateString(result.modified as number, this.locale),
            numViews: result.numViews,
            tags: result.tags,
            categories: result.groupCategories && result.groupCategories.length > 0 ? result.groupCategories : result.categories || [],
            licenseInfo: helpers.createDivHtml(result.licenseInfo as string),
            deployCommonId: helpers.getTypeKeywordProp(result.typeKeywords as string[] || [] as string[], "solutionid-"),
            deployVersion: helpers.getTypeKeywordProp(result.typeKeywords as string[] || [] as string[], "solutionversion-"),
            canDelete: result.created > 1618331400000, // Only solutions deployed after April 13, 2021 at 9:30PM can be deleted (9.1 release)
            canDeploy: (result.typeKeywords || []).indexOf("Display Only") < 0,
            preview: this.preparePreviewImageUrls(result.id as string, result.properties?.preview as cardsHelpers.IPreview[],
              (result.sourcePortalUrl ? result.sourcePortalUrl : this.portalUrl) as string),
            sourcePortalUrl: result.sourcePortalUrl || "",
            knownToBePublicSource: result.knownToBePublicSource || false,
            waitId: "",
            relatedSolutions: common.getProp(result as { [index: string]: any }, "properties.relatedSolutions") as string[] || []
          };

          if (card.deployCommonId !== "" && card.deployVersion !== "") {
            return card;
          }
          else {
            return null;
          }
        }
      );

      // Filter out nulled-out cards & update the paging parameters
      return cards.filter((card: cardsHelpers.ISolutionInfoCard) => card != null);

    } else {
      return [];
    }
  }

  /**
   * Merge a set of search responses into a single response item.
   *
   * @param responseSet Array of search results
   * @returns A response item that takes its query from the first item in the set and sets the other properties
   * as if all of the responses are the result of one, large query that's independent of AGO's 100-item limit.
   */
  private mergeResponses(
    responseSet: Array<portal.ISearchResult<common.IItem>>
  ): portal.ISearchResult<common.IItem> {
    let total = 0;
    responseSet.forEach((response) => total += response.total);

    let results = [] as common.IItem[];
    responseSet.forEach((response) => results = results.concat(response.results));

    return {
      query: responseSet[0].query,
      start: 1,
      num: total,
      nextStart: -1,
      total,
      results
    } as portal.ISearchResult<common.IItem>;
  }

  /**
   * Converts image preview information into a URL pointing to that image; returns non-image previews unchanged.
   *
   * @param id Item id to insert into URL
   * @param preview List of previews to modify
   * @returns Copy of previews with iamge previews modified
   */
  private preparePreviewImageUrls(
    id: string,
    preview: cardsHelpers.IPreview[],
    sourcePortal: string
  ): cardsHelpers.IPreview[] {
    if (!preview) {
      return [] as cardsHelpers.IPreview[];
    }

    return preview.map(
      preview => {
        if (preview.format === "image") {
          preview.name = sourcePortal + "/sharing/rest/content/items/" + id + "/resources/" + preview.name;
        }
        return preview;
      }
    );
  }

}
